"use client";

import React, { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorCount: number;
}

/**
 * Error Boundary qui catch les erreurs DOM causées par Google Translate
 * et force un re-render propre au lieu de crash l'app
 */
export default class TranslateErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(_: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Check si c'est l'erreur Google Translate
    const isTranslateError =
      error.message?.includes("insertBefore") ||
      error.message?.includes("removeChild") ||
      error.message?.includes("appendChild");

    if (isTranslateError) {
      console.warn("[TranslateErrorBoundary] Caught translate-related DOM error, recovering...");
    } else {
      console.error("[TranslateErrorBoundary] Caught error:", error, errorInfo);
    }
  }

  componentDidUpdate(_: Props, prevState: State) {
    // Auto-recover après l'erreur
    if (this.state.hasError && !prevState.hasError) {
      // Reset après un court délai pour permettre le re-render
      setTimeout(() => {
        this.setState((s) => ({
          hasError: false,
          errorCount: s.errorCount + 1
        }));
      }, 50);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render temporaire pendant le recovery
      return (
        <div className="opacity-50 pointer-events-none">
          {this.props.children}
        </div>
      );
    }

    // Key change force un remount propre après recovery
    return (
      <React.Fragment key={this.state.errorCount}>
        {this.props.children}
      </React.Fragment>
    );
  }
}
